import ButtonSpinner from "@components/ButtonSpinner"
import FunnelLayout from "@components/quiz/FunnelLayout"
import QuizHeader from "@components/quiz/QuizHeader"
import StickyFooter from "@components/quiz/StickyFooter"
import createAccount from "@helpers/createAccount"
import { getSignUpEmailFromSessionStorage } from "@helpers/sessionStorage"
import { validatePassword } from "@helpers/validations"
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline"
import { FormEvent, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom"
import { ONBOARDING_BASE_PATH, SignUpEmailState } from "./quizSteps"

type PasswordError = "invalid" | "alreadyRegistered" | "unknown"

export default function PasswordPage() {
  const [password, setPassword] = useState("")
  const [isVisible, setIsVisible] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<PasswordError>()
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()

  // NOTE: The history entry first: it survives a reload when the browser
  // blocks storage. Without either, the email step asks again.
  const email =
    (location.state as Partial<SignUpEmailState> | null)?.email ||
    getSignUpEmailFromSessionStorage()

  if (!email)
    return (
      <Navigate
        replace
        to={`${ONBOARDING_BASE_PATH}/email${location.search}`}
      />
    )

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!validatePassword(password)) {
      setError("invalid")
      return
    }

    setIsSubmitting(true)

    const result = await createAccount({ email, password })

    if (result !== "created") {
      setError(result === "alreadyRegistered" ? "alreadyRegistered" : "unknown")
      setIsSubmitting(false)
      return
    }

    navigate(`/checkout/plan-ready${location.search}`)
  }

  return (
    <FunnelLayout
      header={
        <QuizHeader
          onBack={() => navigate(-1)}
          section="plan"
          sectionProgress={1}
        />
      }
    >
      <form
        className="flex grow animate-slide-in-right flex-col gap-6 pt-4"
        noValidate
        onSubmit={handleSubmit}
      >
        <div className="animate-fade-up space-y-3">
          <h1 className="vm-heading">
            {t("pages.onboarding.password.heading")}
          </h1>
          <p className="vm-subheading">
            {t("pages.onboarding.password.subheading", { email })}
          </p>
        </div>

        <div className="animate-fade-up space-y-3 [animation-delay:120ms]">
          <div className="relative">
            <input
              autoComplete="new-password"
              autoFocus
              className="vm-input pr-12"
              data-invalid={error ? true : undefined}
              onChange={(event) => {
                setPassword(event.target.value)
                setError(undefined)
              }}
              placeholder={t("shared.password")}
              type={isVisible ? "text" : "password"}
              value={password}
            />
            <button
              aria-label={t("pages.onboarding.password.toggleVisibility")}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-vm-muted"
              onClick={() => setIsVisible((current) => !current)}
              type="button"
            >
              {isVisible ? (
                <EyeSlashIcon className="h-5 w-5" />
              ) : (
                <EyeIcon className="h-5 w-5" />
              )}
            </button>
          </div>

          {error ? (
            <p className="animate-fade-in text-sm font-medium text-vm-red">
              {t(`pages.onboarding.password.errors.${error}`)}{" "}
              {error === "alreadyRegistered" && (
                <Link className="underline" to="/account/sign-in">
                  {t("shared.signIn")}
                </Link>
              )}
            </p>
          ) : (
            <p className="text-sm text-vm-muted">
              {t("pages.signUp.password.subheading")}
            </p>
          )}
        </div>

        <StickyFooter>
          <button
            className="vm-button-primary"
            disabled={password.length === 0 || isSubmitting}
            type="submit"
          >
            {isSubmitting && <ButtonSpinner />}
            {t("shared.continue")}
          </button>
        </StickyFooter>
      </form>
    </FunnelLayout>
  )
}
